import classnames from "classnames";
import moment from "moment";
import React from "react";
import { SORTABLE_DATE_FORMAT } from "modules/common/constants";
import { columnLeft, columnsWidth, PlanGrid } from "modules/spending/workload-plan/models/plan-scale";
import { PlanSegment, ProjectPaint, ProjectParticipant } from "../models/project-plan-types";
import { PlanTrack, trackStyles } from "./PlanTrack";
import styles from "./ProjectPlanRowView.module.scss";

/**
 * Уже этой ширины подпись в полосу не влезает. Порог держится ниже ширины дня (30px):
 * однодневный отрезок обязан показывать подпись.
 */
const LABEL_MIN_WIDTH = 26;

/** Ниже этой ширины подпись рисуется компактно: мельче, без отступов, по центру */
const LABEL_TIGHT_WIDTH = 44;

interface ProjectPlanRowViewProps {
    row: ProjectParticipant;
    paint: ProjectPaint;
    /** Номер проекта — подпись полосы, как в плановом табеле */
    label: string;
    grid: PlanGrid;
    monthBreaks: number[];
    selectedSlot: { start: number; length: number } | null;
}

/** Строка графика участника проекта. Только показ: ни жестов, ни выделения ячеек */
export const ProjectPlanRowView: React.FC<ProjectPlanRowViewProps> = React.memo((props) => {
    const { row, paint, label, grid, monthBreaks, selectedSlot } = props;

    const outsourced = row.kind === "outsourcer";

    // У подрядчика срок договора — главное, что нужно видеть, поэтому он стоит
    // в липкой колонке рядом с номером договора: у полосы его унесло бы
    // горизонтальной прокруткой. У сотрудника вторая строка — должность.
    const note = outsourced ? withStopDay(row.note, row.segments) : row.note;

    return (
        <div className={styles.row}>
            <div className={styles.name} data-tip={row.label} data-tip-note={note}>
                <div className={styles.label}>{row.label}</div>
                <div className={styles.note}>{note}</div>
            </div>

            <PlanTrack
                grid={grid}
                monthBreaks={monthBreaks}
                selectedSlot={selectedSlot}
                className={styles.track}
            >
                {row.segments.map((segment) => {
                    const left = columnLeft(grid, segment.start);
                    const width = columnsWidth(grid, segment.start, segment.length);

                    return (
                        <div
                            key={segment.start}
                            className={classnames(trackStyles.block, {
                                [trackStyles.blockOutsourced]: outsourced,
                                [trackStyles.blockTight]: width < LABEL_TIGHT_WIDTH,
                                [trackStyles.blockDark]: paint.dark,
                                [trackStyles.blockLight]: !paint.dark,
                                [trackStyles.cutLeft]: segment.continuesLeft,
                                [trackStyles.cutRight]: segment.continuesRight,
                            })}
                            style={{
                                left,
                                width,
                                backgroundColor: paint.background,
                                borderColor: paint.borderColor,
                            }}
                            data-tip={row.label}
                            data-tip-note={describe(row.note, segment)}
                        >
                            {width >= LABEL_MIN_WIDTH && (
                                <span className={trackStyles.blockLabel}>{label}</span>
                            )}
                        </div>
                    );
                })}
            </PlanTrack>
        </div>
    );
});

ProjectPlanRowView.displayName = "ProjectPlanRowView";

/** Номер договора со сроком: «Договор 457 · до 30.10.2026» */
function withStopDay(note: string, segments: PlanSegment[]): string {
    const last = segments.reduce(
        (result, segment) => (segment.stopDay > result ? segment.stopDay : result),
        ""
    );

    if (!last) {
        return note;
    }

    const date = `до ${moment(last, SORTABLE_DATE_FORMAT).format("DD.MM.YYYY")}`;

    return note ? `${note} · ${date}` : date;
}

/**
 * Подсказка отрезка. Срок берётся из настоящей длины, а не из видимой: у договора,
 * который не помещается в окно, иначе показывалось бы «сколько влезло на экран».
 */
function describe(note: string, segment: PlanSegment): string {
    const parts: string[] = [];

    if (note) {
        parts.push(note);
    }

    if (segment.workDays > 0) {
        parts.push(`${segment.workDays} раб. дн.`);
    }

    if (segment.stopDay) {
        parts.push(`до ${moment(segment.stopDay, SORTABLE_DATE_FORMAT).format("DD.MM.YYYY")}`);
    }

    return parts.join(" · ");
}
